<template>
  <Teleport to="body" :disabled="inline">
    <div
      :class="
        inline ? 'contents' : 'fixed inset-0 z-50 flex items-center justify-center bg-black/40'
      "
      @click.self="!inline && emit('close')"
    >
      <div
        ref="modalEl"
        data-testid="location-map-modal"
        class="surface flex flex-col"
        :class="
          inline
            ? 'mt-2 w-full gap-y-3 rounded-md border border-gray-200 p-3 shadow-sm'
            : 'relative max-h-[min(42rem,calc(100dvh-2rem))] w-[min(36rem,calc(100vw-1rem))] justify-between gap-y-2 rounded-md p-2 shadow-xl'
        "
        :style="inline ? undefined : modalStyle"
        @click.stop
      >
        <div v-if="!inline" class="flex items-center justify-between px-1">
          <span class="font-heading text-xl select-none">Locatie op de kaart</span>
          <GeneralButton
            title="Sluiten"
            color="surface"
            size="large"
            variant="transparent"
            :icon="['fas', 'xmark']"
            class="absolute right-0"
            @click="emit('close')"
          />
        </div>

        <GeneralButton
          v-if="showMapsAppButton"
          color="secondary"
          data-testid="open-in-maps"
          :icon="['fas', 'location-arrow']"
          class="w-full"
          @click="openInMaps"
        >
          Navigeren in kaarten-app
        </GeneralButton>

        <div
          class="flex min-h-0 flex-col gap-y-3"
          :class="inline ? undefined : 'flex-1 overflow-y-auto px-1'"
          data-testid="location-editor-form"
        >
          <p v-if="canEditLocation && !inline" class="text-sm text-gray-500">
            Sleep de markering om de locatie aan te passen.
          </p>

          <div
            ref="mapEl"
            data-testid="location-map"
            class="h-72 w-full overflow-hidden rounded-md border border-gray-200"
          />

          <div class="primary-lighten-1 flex flex-col gap-y-2 rounded-md p-2">
            <label class="block">
              <span class="mb-1 block text-sm">Adres</span>
              <LocationAutocompleteInput
                v-model="address"
                data-testid="location-address"
                placeholder="Zoek een adres…"
                :disabled="!canEditLocation || isGeocoding"
                :exclude-id="location.id"
                @select="onAddressSelected"
              />
            </label>

            <label class="block">
              <span class="mb-1 block text-sm">Locatienaam</span>
              <InputText
                v-model="name"
                data-testid="location-name-input"
                placeholder="Locatienaam"
                :disabled="!canEditLocation"
                @keydown.enter.prevent="onSave"
              />
            </label>
          </div>
        </div>

        <div v-if="canEditLocation" class="flex grow-0 justify-end gap-x-2 pt-2">
          <button type="button" class="cursor-pointer px-3 py-1 text-sm" @click="emit('close')">
            Annuleren
          </button>
          <GeneralButton
            :disabled="saving"
            data-testid="location-save"
            class="min-w-24"
            @click="onSave"
          >
            Opslaan
          </GeneralButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { onKeyStroke } from "@vueuse/core"
import { Location } from "@/models/location.model"
import type { LocationController } from "@/controllers/model-controllers/location-controller"
import type { LocationSuggestion } from "@/composables/location/use-location-autocomplete"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import InputText from "@/components/inputs/InputText.vue"
import LocationAutocompleteInput from "@/components/inputs/LocationAutocompleteInput.vue"
import { useGoogleMaps } from "@/composables/google-maps.ts"
import { isMediumMq } from "@/helper/app/device-helper"
import {
  buildMapsNavigationUrl,
  createGoogleLocationPinContent,
  formatLocationMapPinLabel,
  forwardGeocode,
  openMapsNavigation,
  reverseGeocode
} from "@/helper/location/location-helpers"
import { useAuthStore } from "@/store/auth.store"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import { RoleTypes } from "@/enums/role-types"

const props = withDefaults(
  defineProps<{ location: Location; anchor?: { x: number; y: number } | null; inline?: boolean }>(),
  { inline: false }
)
const emit = defineEmits<{ close: []; saved: [location: Location] }>()

const { activeUser } = storeToRefs(useAuthStore())

onKeyStroke("Escape", () => {
  if (!props.inline) emit("close")
})

const canEditLocation = computed(
  () => !!activeUser.value?.knownBusinessEntityContact?.hasRole(RoleTypes.PLANNER)
)
const showMapsAppButton = computed(() => !props.inline && !canEditLocation.value)

const DEFAULT_CENTER = { lat: 52.1, lng: 5.1 }
const MAP_ID = "bcb83b04c4c522f9"
const COORD_EPSILON = 1e-6

const modalEl = ref<HTMLElement>()
const modalStyle = ref<Record<string, string>>({ opacity: "0" })
const mapEl = ref<HTMLElement>()

let map: google.maps.Map | null = null
let marker: google.maps.marker.AdvancedMarkerElement | null = null
let GoogleMarker: typeof google.maps.marker.AdvancedMarkerElement
let GooglePinElement: typeof google.maps.marker.PinElement

// --- Location editor state (name/address/coords + geocoding + save) ---

const locationController = inject<LocationController>("locationController")
const dialogStore = useDialogStore()

const name = ref("")
const address = ref("")
const lat = ref<number | null>(null)
const lng = ref<number | null>(null)
const saving = ref(false)
const isGeocoding = ref(false)

let initialLat = 0
let initialLng = 0
let initialName = ""
let geocodeGeneration = 0

watch(
  () => props.location,
  (location) => {
    initialLat = location.latitude ?? 0
    initialLng = location.longitude ?? 0
    initialName = (location.name ?? "").trim()
    name.value = location.name ?? ""
    address.value = location.address ?? ""
    lat.value = location.latitude ?? null
    lng.value = location.longitude ?? null
    void renderLocationPin()
  },
  { immediate: true, deep: true }
)

function apiKey(): string {
  return activeUser.value?.activeTenant?.tenantSettings?.apiKeyGoogleMaps ?? ""
}

async function updateAddressForPosition(nextLat: number, nextLng: number) {
  if (!apiKey()) return

  const generation = ++geocodeGeneration
  isGeocoding.value = true
  try {
    const nextAddress = await reverseGeocode(nextLat, nextLng, apiKey())
    if (generation !== geocodeGeneration) return
    if (nextAddress) address.value = nextAddress
  } finally {
    if (generation === geocodeGeneration) isGeocoding.value = false
  }
}

function setPosition(nextLat: number, nextLng: number) {
  lat.value = nextLat
  lng.value = nextLng
  void updateAddressForPosition(nextLat, nextLng)
}

async function geocodeAddress(nextAddress: string) {
  if (!apiKey() || !nextAddress.trim()) return

  const generation = ++geocodeGeneration
  isGeocoding.value = true
  try {
    const coords = await forwardGeocode(nextAddress, apiKey())
    if (generation !== geocodeGeneration) return
    address.value = nextAddress
    if (coords) {
      lat.value = coords.lat
      lng.value = coords.lng
    }
  } finally {
    if (generation === geocodeGeneration) isGeocoding.value = false
  }
}

async function persistSave(): Promise<Location | null> {
  const location = props.location
  if (lat.value == null || lng.value == null) return null

  saving.value = true
  try {
    location.name = name.value.trim() || null
    location.address = address.value || null
    location.latitude = lat.value
    location.longitude = lng.value

    if (locationController) {
      const updated = location.isSaved
        ? await locationController.update(location)
        : await locationController.create(location)
      return updated ?? location
    }
    return location
  } finally {
    saving.value = false
  }
}

function save(): Promise<Location | null> {
  const moved =
    lat.value != null &&
    lng.value != null &&
    (Math.abs(lat.value - initialLat) > COORD_EPSILON ||
      Math.abs(lng.value - initialLng) > COORD_EPSILON)
  const renamed = name.value.trim() !== initialName

  if (moved && !renamed && address.value.trim() && initialName) {
    return new Promise((resolve) => {
      dialogStore.currentDialog = {
        title: "Locatienaam bijwerken?",
        body: `De locatie is verplaatst naar "${address.value}". Wil je de locatienaam ook bijwerken naar dit adres?`,
        type: DialogTypes.CONFIRM,
        textConfirm: "Ja, bijwerken",
        textCancel: "Nee, behoud huidige naam",
        onConfirm: async () => {
          name.value = address.value
          resolve(await persistSave())
        },
        onCancel: async () => resolve(await persistSave())
      }
    })
  }
  return persistSave()
}

async function onAddressSelected(item: LocationSuggestion) {
  if (!canEditLocation.value) return
  if (item.source === "saved" && item.location) {
    // Adopt the saved location's address + coordinates without re-geocoding.
    geocodeGeneration++
    address.value = item.location.address ?? item.label
    if (item.location.latitude != null && item.location.longitude != null) {
      lat.value = item.location.latitude
      lng.value = item.location.longitude
    }
    return
  }
  if (!name.value.trim() || name.value.trim() === address.value.trim()) {
    name.value = ""
  }
  await geocodeAddress(item.label)
}

async function onSave() {
  if (!canEditLocation.value) return
  const updated = await save()
  if (updated) {
    emit("saved", updated)
    emit("close")
  }
}

// --- Modal positioning + map rendering ---

function positionModal() {
  const el = modalEl.value
  if (!el) return
  modalStyle.value = { opacity: "1" }
}

function pinCoords() {
  return {
    lat: lat.value ?? DEFAULT_CENTER.lat,
    lng: lng.value ?? DEFAULT_CENTER.lng
  }
}

async function renderLocationPin() {
  if (!mapEl.value) return

  if (!GoogleMarker) {
    const { importLibrary } = useGoogleMaps()
    const { Map } = await importLibrary("maps")
    const { AdvancedMarkerElement, PinElement } = await importLibrary("marker")
    GoogleMarker = AdvancedMarkerElement
    GooglePinElement = PinElement

    if (props.location.latitude == null || props.location.longitude == null) {
      const savedAddress = props.location.address?.trim()
      if (savedAddress) {
        await geocodeAddress(savedAddress)
      } else {
        setPosition(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng)
      }
    }

    map = new Map(mapEl.value, {
      zoom: 15,
      center: pinCoords(),
      mapId: MAP_ID,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      streetViewControl: false,
      scaleControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      gestureHandling: isMediumMq() ? "cooperative" : "auto"
    })

    map.addListener("click", (event: google.maps.MapMouseEvent) => {
      if (!canEditLocation.value || !event.latLng) return
      setPosition(event.latLng.lat(), event.latLng.lng())
    })
  }

  const coords = pinCoords()
  if (marker) {
    google.maps.event.clearInstanceListeners(marker)
    marker.map = null
  }

  marker = new GoogleMarker({
    map,
    position: coords,
    content: createGoogleLocationPinContent(
      GooglePinElement,
      "map-marker",
      formatLocationMapPinLabel(props.location)
    ),
    gmpDraggable: canEditLocation.value
  })

  marker.addListener("dragend", () => {
    if (!canEditLocation.value) return
    const position = marker?.position
    if (!position) return
    const nextLat = typeof position.lat === "function" ? position.lat() : position.lat
    const nextLng = typeof position.lng === "function" ? position.lng() : position.lng
    setPosition(nextLat as number, nextLng as number)
  })

  map!.panTo(coords)
  if (props.inline && map) {
    google.maps.event.trigger(map, "resize")
    map.panTo(coords)
  }
}

onMounted(async () => {
  await nextTick()
  if (!props.inline) {
    positionModal()
  } else {
    modalEl.value?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }
  await renderLocationPin()

  watch(
    () => (lat.value != null && lng.value != null ? `${lat.value},${lng.value}` : null),
    () => void renderLocationPin()
  )
})

onBeforeUnmount(() => {
  if (marker) {
    google.maps.event.clearInstanceListeners(marker)
    marker.map = null
    marker = null
  }
  if (map) {
    google.maps.event.clearInstanceListeners(map)
    map = null
  }
})

function openInMaps() {
  const url = buildMapsNavigationUrl(
    lat.value ?? props.location.latitude,
    lng.value ?? props.location.longitude,
    props.location.address
  )
  openMapsNavigation(url)
}
</script>
